
import { useState } from 'react';

interface WelcomeMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupName: string;
  welcomeMessage: string;
  groupCoverImage: string;
  onDismiss: () => void;
}

const WelcomeMessageModal = ({
  isOpen,
  onClose,
  groupName,
  welcomeMessage,
  groupCoverImage,
  onDismiss,
}: WelcomeMessageModalProps) => {
  const [isClosing, setIsClosing] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onDismiss();
      onClose();
    }, 250);
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        {/* Cover Image Header */}
        <div className="relative h-36 overflow-hidden">
          <img
            src={groupCoverImage}
            alt={groupName}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-sparkle-fill text-amber-400 text-sm"></i>
              </div>
              <span className="text-amber-300 text-xs font-semibold uppercase tracking-wider">Welcome!</span>
            </div>
            <h2 className="text-xl font-bold text-white leading-tight">{groupName}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-chat-smile-3-fill text-teal-500 text-lg"></i>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900 mb-1">Message from the group</p>
              <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                {welcomeMessage}
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-gray-50 rounded-xl p-4 mb-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Quick Tips</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-3-line text-teal-600 text-xs"></i>
                </div>
                <span className="text-xs text-gray-600">Start a discussion to introduce yourself</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-teal-600 text-xs"></i>
                </div>
                <span className="text-xs text-gray-600">Check the group rules in the sidebar</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-add-line text-teal-600 text-xs"></i>
                </div>
                <span className="text-xs text-gray-600">Invite friends who might be interested</span>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={handleClose}
            className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            Got it, let&apos;s go!
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeMessageModal;
